import React, { useState, useEffect } from "react";
import axios from "axios";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import * as Yup from "yup";

const JobAdd = () => {
  const todayDate = new Date().toISOString().split('T')[0];

  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState({ company: "", hr: "" });
  const [filteredResults, setFilteredResults] = useState({ companies: [], hrs: [] });
  const [showDropdown, setShowDropdown] = useState({ company: false, hr: false });

  const jobsInitialState = {
    job_name: "",
    job_category: "",
    company_id: "",
    company_name: "",
    hr_id: "",
    hr_name: "",
    post_date: todayDate,
    end_date: todayDate,
  };

  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loader, setLoader] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [hrs, setHrs] = useState([]);

  const validationSchema = Yup.object().shape({
    job_name: Yup.string()
      .required("Job name is required")
      .min(3, "Job name must be at least 3 characters long"),
    company_id: Yup.string().required("Company selection is required"),
    company_name: Yup.string().required("Company name is required"),
    post_date: Yup.date()
      .required("Post date is required")
      .min(todayDate, "Post date cannot be in the past"),
    end_date: Yup.date().required("End date is required"),
  });



  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}companyDetails/companylist`);
      setCompanies(response.data.companies);
    } catch (error) {
      setError("Failed to fetch companies. Please try again later.");
    }
  };

  const fetchHrs = async (company_id) => {
    if (!company_id) return; 
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}hr_details/showallhr_details/${company_id}`);
      setHrs(response?.data);
    } catch (error) {
      setError("Failed to fetch HR details. Please try again later.");
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchHrs();
  }, []);

  const handleSearch = (type, value) => {
    setSearchTerm({ ...searchTerm, [type]: value });
    setShowDropdown({ ...showDropdown, [type]: true });

    const filtered = type === 'company'
      ? companies.filter(company =>
        company.company_name.toLowerCase().includes(value.toLowerCase())
      )
      : hrs.filter(hr =>
        hr.name.toLowerCase().includes(value.toLowerCase())
      );

    setFilteredResults({
      ...filteredResults,
      [type === 'company' ? 'companies' : 'hrs']: filtered
    });
  };

  const handleSelect = (type, item, setFieldValue) => {
    if (type === 'company') {
      setFieldValue('company_id', item.company_id);
      setFieldValue('company_name', item.company_name);
      setSearchTerm({ ...searchTerm, company: item.company_name, hr: '' });
      setFieldValue('hr_id', '');
      setFieldValue('hr_name', '');
      //setSearchTerm({ ...searchTerm, hr: '' });
      setFilteredResults({ ...filteredResults, hrs: [] });
      fetchHrs(item.company_id);
    } else {
      setFieldValue('hr_id', item.userid);
      setFieldValue('hr_name', item.name);
      setSearchTerm({ ...searchTerm, hr: item.name });
    }
    setShowDropdown({ ...showDropdown, [type]: false });
  };

  const callApiJobAdd = async (values) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}jobs/createjob`;
      const response = await axios.post(url, values);

      if (response.status === 201) {
        const { job_id } = response.data;
        navigate(`/jobdetailsadd/${job_id}`);
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      setError("Failed to create job. Please try again later.");
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center">
              <h4>Create New Job</h4>
            </Card.Header>
            <Card.Body>
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              <Formik
                initialValues={jobsInitialState}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting }) => {
                  console.log(values);
                  callApiJobAdd(values);
                  setSubmitting(false);
                }}
              >
                {({ values, setFieldValue, touched, errors, isSubmitting }) => (
                  <Form>
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="job_name" className="form-label">
                          Job Name<span className="text-danger">*</span>
                        </label>
                        <Field
                          name="job_name"
                          type="text"
                          className={`form-control ${touched.job_name && errors.job_name ? 'is-invalid' : ''}`}
                          placeholder="Enter Job Name"
                        />
                        <ErrorMessage
                          name="job_name"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label className="form-label">Company<span className="text-danger">*</span></label>
                        <div className="position-relative">
                          <input
                            type="text"
                            className={`form-control ${touched.company && errors.company ? 'is-invalid' : ''}`}
                            value={searchTerm.company}
                            onChange={(e) => handleSearch('company', e.target.value)}
                            placeholder="Search for a company..."
                            autoComplete="off"
                          />
                          {showDropdown.company && searchTerm.company && (
                            <div className="position-absolute w-100 mt-1 shadow bg-white border rounded" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                              {filteredResults.companies.map((company) => (
                                <div
                                  key={company.company_id}
                                  className="p-2 hover-bg-light cursor-pointer"
                                  onClick={() => handleSelect('company', company, setFieldValue)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {company.company_name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {values.company_id && (
                          <input
                            type="text"
                            className="form-control mt-2"
                            value={values.company_id}
                            disabled
                            placeholder="Company ID"
                          />
                        )}
                        {touched.company && errors.company && (
                          <div className="text-danger mt-1">{errors.company}</div>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <label className="form-label">HR</label>
                        <div className="position-relative">
                          <input
                            type="text"
                            className={`form-control ${touched.hr && errors.hr ? 'is-invalid' : ''}`}
                            value={searchTerm.hr}
                            onChange={(e) => handleSearch('hr', e.target.value)}
                            placeholder="Search for an HR..."
                            autoComplete="off"
                          />
                          {showDropdown.hr && searchTerm.hr && (
                            <div className="position-absolute w-100 mt-1 shadow bg-white border rounded" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                              {filteredResults.hrs.map((hr) => (
                                <div
                                  key={hr.hr_id}
                                  className="p-2 hover-bg-light cursor-pointer"
                                  onClick={() => handleSelect('hr', hr, setFieldValue)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {hr.name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <label className="form-label">HR ID</label>
                        <Field
                          name="hr_id"
                          type="text"
                          className={`form-control ${touched.hr && errors.hr ? 'is-invalid' : ''}`}
                          placeholder="Enter HR id"
                        >
                        </Field>
                        <ErrorMessage
                          name="hr_id"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="post_date" className="form-label">
                          Post Date<span className="text-danger">*</span>
                        </label>
                        <Field
                          name="post_date"
                          type="date"
                          className={`form-control ${touched.post_date && errors.post_date ? 'is-invalid' : ''}`}
                          min={todayDate}
                        />
                        <ErrorMessage
                          name="post_date"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="end_date" className="form-label">
                          End Date<span className="text-danger">*</span>
                        </label>
                        <Field
                          name="end_date"
                          type="date"
                          className={`form-control ${touched.end_date && errors.end_date ? 'is-invalid' : ''}`}
                          min={todayDate}
                        />
                        <ErrorMessage
                          name="end_date"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col className="text-center">
                        <Button
                          type="submit"
                          variant="success"
                          disabled={isSubmitting}
                          className="me-2"
                        >
                          {isSubmitting ? 'Creating...' : 'Create New Job'}
                        </Button>
                        <Button variant="danger" onClick={() => navigate(-1)}>
                          Cancel
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default JobAdd;